import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FlashMessagesService } from 'flash-messages-angular';
import { Subject } from 'rxjs';
import { PropertiesService } from 'src/app/services/properties.service';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { PropertyFheDialogComponent } from './property-fhe-dialog/property-fhe-dialog.component';
import { DataTableDirective } from 'angular-datatables';
import { DatatablesHelper } from 'src/app/helpers';
import { UploadPropertiesDialogComponent } from 'src/app/shared/dialogs/upload-properties-dialog/upload-properties-dialog.component';
import { Property } from 'src/app/models';
import { RouteService } from 'src/app/core/services';

type UIProperty = Property & {
  saving?: boolean;
};

@Component({
  selector: 'app-properties-fhe',
  templateUrl: './properties-fhe.component.html',
  styleUrls: ['./properties-fhe.component.css']
})
export class PropertiesFheComponent implements OnInit, OnDestroy {
  properties: any[] = [];
  loaded = false;

  // Has to be 'any' for the Buttons extension to work,
  // the DataTables.Settings type doesn't recognize the configuration for the extensions
  dtOptions: any = {};

  // We use this trigger because fetching the properties can take a while,
  // thus we ensure the data is fetched before rendering
  dtTrigger: Subject<any> = new Subject<any>();

  @ViewChild(DataTableDirective, {static: false})
  dtElement!: DataTableDirective;

  constructor(private propertiesService: PropertiesService, private routeService: RouteService, private flashMessage: FlashMessagesService, private dialog: MatDialog) {}

  ngOnInit(): void {
    this.initTable();
    this.loadTable();
  }

  ngOnDestroy(): void {
    // Do not forget to unsubscribe the event
    this.dtTrigger.unsubscribe();
  }

  private initTable(): void {
    this.dtOptions = {
      pagingType: 'full_numbers',
      pageLength: 10,
      // enables horizontal scrolling on datatable
      scrollX: true,

      // Declare the use of the extension in the dom parameter and give a class for the css (can't get the css to apply though so commented out)
      // dom: '<"table-buttons"lBfr>tip',
      dom: 'lBfrtip',

      // Configure the buttons
      buttons: [
        'colvis',
        'excel'
      ]
    };
  }

  // Manually rerender table after changes
  private async loadTable() {
    // Get the properties for this user
    const res: any = await this.propertiesService.getSelfProperties().toPromise();

    // Assign the data
    this.properties = res.properties || [];

    if (this.loaded) {
      this.rerender();
    } else {
      this.loaded = true;

      // Calling the DT trigger to manually render the table
      this.dtTrigger.next();
    }
  }

  private rerender(): void {
    DatatablesHelper.redrawTable(this.dtElement, this.dtTrigger);
  }

  addPropertyDialog() {
    const dialogConfig = new MatDialogConfig();

    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;

    dialogConfig.data = {
      title: 'Add Property',
    };

    const dialogRef = this.dialog.open(PropertyFheDialogComponent, dialogConfig);

    dialogRef.afterClosed().toPromise().then((propertyInfo: any) => {
      if (!propertyInfo) return;

      // Add Property
      this.propertiesService.addProperty(propertyInfo).toPromise().then((data: any) => {

        // Rerender manually
        this.loadTable();

        // Success
        if (data.success) {
          // Display success
          this.flashMessage.show(data.message || "Property added.", {
            cssClass: 'alert-success',
            timeout: 3000
          });
        }
      });
    });
  }

  uploadPropertiesDialog() {
    const dialogConfig = new MatDialogConfig();

    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;

    // Open dialog
    const dialogRef = this.dialog.open(UploadPropertiesDialogComponent, dialogConfig);

    dialogRef.afterClosed().toPromise().then(async (res: any) => {
      if (!res || !res.properties.length) return;

      this.routeService.scrollToTop();

      const properties = res.properties;

      // Register Properties
      const data: any = await this.propertiesService.addManyProperties(properties).toPromise();

      // Reload table data
      await this.loadTable().catch(() => {});

      // Success
      if (data.success) {
        // Display success
        this.flashMessage.show(data.message || 'Properties registered.', {
          cssClass: 'alert-success',
          timeout: 3000
        });
      }
    });
  }

  editProperty(property: any){
    const dialogConfig = new MatDialogConfig();

    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;

    dialogConfig.data = {
      title: 'Edit Property',

      // Gotta send all the property data to the dialog to prefill
      date:                  property.date,
      isAvailable:           property.isAvailable,
      ownerName:             property.ownerName,
      managerName:           property.managerName,
      contactPhone:          property.contactPhone,
      email:                 property.email,
      address:               property.address,
      city:                  property.city,
      zipCode:               property.zipCode,
      buildingNumber:        property.buildingNumber,
      buildingUnits:         property.buildingUnits,
      aptLic:                property.aptLic,
      availableUnits:        property.availableUnits,
      propertyNote:          property.propertyNote,
      hcvCheck:              property.hcvCheck,
      hcvNote:               property.hcvNote,
      cuCheck:               property.cuCheck,
      cuUnits:               property.cuUnits,
      cuProvider:            property.cuProvider,
      cuNote:                property.cuNote,
      lsCheck:               property.lsCheck,
      lsProvider:            property.lsProvider,
      lsUnits:               property.lsUnits,
      lsNote:                property.lsNote,
      latitude:              property.latitude,
      longitude:             property.longitude,
      buildingAdaAccessible: property.buildingAdaAccessible,
      unitAdaAccessible:     property.unitAdaAccessible,
      accessibleNote:        property.accessibleNote,
      incentiveForRentwise:  property.incentiveForRentwise,
      incentiveNote:         property.incentiveNote,
    };

    const dialogRef = this.dialog.open(PropertyFheDialogComponent, dialogConfig);

    dialogRef.afterClosed().toPromise().then((propertyInfo: any) => {
      if (!propertyInfo) return;

      // Update Property
      this.propertiesService.updateProperty(property._id, propertyInfo).toPromise().then((data: any) => {

        // Rerender manually
        this.loadTable();

        // Success
        if (data.success) {
          // Display success
          this.flashMessage.show(data.message || "Property updated.", {
            cssClass: 'alert-success',
            timeout: 3000
          });

        }
      });
    });
  }

  deleteProperty(property: UIProperty){
    if (confirm(`Are you sure you want to permanently delete property from your list at address ${property.address}?`) !== true) {
      return;
    }

    // Delete Property
    this.propertiesService.deleteProperty(property._id).toPromise().then((data: any) => {

      // Rerender manually
      this.loadTable();

      // Success
      if (data.success) {
        // Display success
        this.flashMessage.show("Property deleted.", {
          cssClass: 'alert-success',
          timeout: 3000
        });
      }
    });
  }

  async saveAvailability(property: UIProperty) {
    const newValue = property.isAvailable;

    try {
      property.saving = true;
      await this.propertiesService.updateProperty(property._id, { isAvailable: newValue }).toPromise();
    } finally {
      property.saving = false;
    }
  }

  sortDate(value: any) {
    return value ? +new Date(value) : 0;
  }
}
